/**
 * Stale-tab-after-deploy handler.
 *
 * When Vite builds, every code-split chunk gets a content-hashed filename
 * (e.g. `BarrelDetail-DUEufDmG.js`). After a new deploy, the old hashed
 * files are removed from the CDN — so any browser tab that's been open
 * since before the deploy will hit a 404 the moment the user navigates
 * to a route that lazy-loads a chunk.
 *
 * The error class is "Failed to fetch dynamically imported module" /
 * "Loading chunk N failed" / "Importing a module script failed". We
 * catch all three at the window level and show a one-time prompt
 * offering to reload the page (which fetches the fresh index.html and
 * its new chunk references).
 *
 * This module is purely a UX handler — it does NOT report to Sentry.
 * The matching `ignoreErrors` patterns in sentry.ts suppress the
 * Sentry side.
 */

const STALE_TAB_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Importing a module script failed/i,
  // Safari variants
  /error loading dynamically imported module/i,
  // MIME-type-mismatch case: when Vercel returns the SPA's index.html
  // for a missing chunk path, the browser gets HTML where it expected
  // JS and reports this distinctive error.
  // Chrome/Safari phrasing:
  /Expected a JavaScript-or-Wasm module script/i,
  // Firefox phrasing — same root cause (HTML returned for a JS
  // import), different error text. Example:
  //   "'text/html' is not a valid JavaScript MIME type."
  /is not a valid JavaScript MIME type/i,
];

let prompted = false;

// sessionStorage key + window for the loop guard. sessionStorage
// survives a reload within the same tab, so if we hard-reset and STILL
// hit a stale-chunk error within this window, the reset didn't help
// (a genuinely broken deploy, not a stale cache) and we stop
// auto-reloading — otherwise the user is trapped in a refresh loop.
const HARD_RESET_KEY = 'twb:staleReloadAt';
const HARD_RESET_WINDOW_MS = 30_000;

/**
 * Exported so ErrorBoundary can also detect stale-tab errors that come
 * through React's render path (which doesn't trigger window.onerror).
 */
export const isStaleTabError = (msg: string | undefined): boolean => {
  if (!msg) return false;
  return STALE_TAB_PATTERNS.some((p) => p.test(msg));
};

/**
 * Tear down the stale service worker + caches, then reload. A plain
 * reload re-serves the same stale `index.html` from the SW precache
 * (which references chunk files the server has since purged), so the
 * import fails again and we loop. Unregistering the SW + clearing the
 * Cache Storage forces the reload to fetch a fresh index + matching
 * chunks from the network. Best-effort: any failure still falls through
 * to the reload.
 */
const hardReset = async (): Promise<void> => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* ignore — fall through to reload */
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore — fall through to reload */
  }
  window.location.reload();
};

/**
 * Exported so ErrorBoundary can fire the same recovery directly.
 *
 * Behavior: automatically hard-reset (clear the SW + caches and reload)
 * the first time a stale-chunk error is seen — no prompt, since the
 * recovery is safe and the alternative is a broken page. The module-level
 * `prompted` flag dedupes within a page load; the `sessionStorage` guard
 * dedupes ACROSS the reload so we attempt the hard reset at most once per
 * ~30s. If we're still failing after that, we bail out and let
 * ErrorBoundary's manual "Reload" UI stand instead of looping.
 */
export const promptStaleTabReload = (): void => {
  if (prompted) return;
  prompted = true;

  let lastReset = 0;
  try {
    lastReset = Number(sessionStorage.getItem(HARD_RESET_KEY)) || 0;
  } catch {
    /* sessionStorage unavailable — treat as no prior reset */
  }
  // Already hard-reset recently and STILL broken → don't loop. The
  // ErrorBoundary render path shows its manual "New version available —
  // Reload" UI; the window-handler path simply stops here.
  if (lastReset && Date.now() - lastReset < HARD_RESET_WINDOW_MS) {
    return;
  }
  try {
    sessionStorage.setItem(HARD_RESET_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  void hardReset();
};

export const installStaleTabReloadHandler = (): void => {
  // Catches synchronous dynamic-import failures + any thrown error
  // whose message matches the stale-tab pattern.
  window.addEventListener('error', (event: ErrorEvent) => {
    if (isStaleTabError(event.message ?? event.error?.message)) {
      promptStaleTabReload();
    }
  });

  // Catches unhandled promise rejections from `import()` calls — which
  // is how React.lazy + react-router surface chunk-load failures.
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const msg =
      typeof reason === 'string'
        ? reason
        : reason instanceof Error
          ? reason.message
          : undefined;
    if (isStaleTabError(msg)) {
      promptStaleTabReload();
    }
  });
};
